import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../store';
import { useSocket } from '../../hooks/useSocket';
import { useQueryClient } from '@tanstack/react-query';
import { getAlerts } from '../../utils/api';
import toast from 'react-hot-toast';

const NAV_ADMIN = [
  { to: '/dashboard', label: 'Dashboard', icon: IconGrid },
  { to: '/pos', label: 'POS Terminal', icon: IconPOS },
  { to: '/inventory', label: 'Inventory', icon: IconBox },
  { to: '/sales', label: 'Sales', icon: IconBag },
  { to: '/employees', label: 'Employees', icon: IconUsers },
  { to: '/reports', label: 'Reports', icon: IconBar },
  { to: '/settings', label: 'Settings', icon: IconCog },
  { to: '/alerts', label: 'Alerts', icon: IconBell, badge: true },
];
const NAV_SUPERVISOR = [
  { to: '/dashboard', label: 'Dashboard', icon: IconGrid },
  { to: '/pos', label: 'POS Terminal', icon: IconPOS },
  { to: '/inventory', label: 'Inventory', icon: IconBox },
  { to: '/sales', label: 'Sales', icon: IconBag },
  { to: '/employees', label: 'Staff', icon: IconUsers },
  { to: '/reports', label: 'Reports', icon: IconBar },
  { to: '/alerts', label: 'Alerts', icon: IconBell, badge: true },
];
const NAV_CASHIER = [
  { to: '/pos', label: 'POS Terminal', icon: IconPOS },
  { to: '/sales', label: 'My Sales', icon: IconBag },
  { to: '/alerts', label: 'Alerts', icon: IconBell, badge: true },
];

const NAV_BY_ROLE = { admin: NAV_ADMIN, supervisor: NAV_SUPERVISOR, cashier: NAV_CASHIER };

export default function AppShell() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const { data: alerts = [] } = useQuery({ queryKey: ['alerts'], queryFn: () => getAlerts().then((response) => response.data), refetchInterval: 30000 });
  const alertCount = alerts.length;
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    function handleResize() {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileNavOpen(false);
      }
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useSocket({
    'sale:new': (data) => {
      qc.invalidateQueries({ queryKey: ['sales'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      if (user?.role !== 'cashier' || data.sale?.cashier_id !== user?.id) {
        toast.success(`New sale: KES ${Number(data.sale?.total || 0).toLocaleString()}`, { duration: 3000 });
      }
    },
    'stock:updated': () => qc.invalidateQueries({ queryKey: ['products'] }),
    'product:created': () => qc.invalidateQueries({ queryKey: ['products'] }),
    'product:updated': () => qc.invalidateQueries({ queryKey: ['products'] }),
    'product:deleted': () => qc.invalidateQueries({ queryKey: ['products'] }),
    'sale:refunded': () => qc.invalidateQueries({ queryKey: ['sales'] }),
  });

  const navItems = NAV_BY_ROLE[user?.role] || NAV_CASHIER;
  const pageTitle = navItems.find((n) => location.pathname.startsWith(n.to))?.label || 'Daima POS';
  const avClass = { admin: 'av-blue', supervisor: 'av-teal', cashier: 'av-green' }[user?.role] || 'av-blue';

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className={`app-shell ${isMobile ? 'mobile-shell' : ''}`}>
      {isMobile && mobileNavOpen && <button className="mobile-nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" />}

      <aside className={`sidebar ${collapsed ? 'is-collapsed' : ''} ${isMobile ? 'is-mobile' : ''} ${mobileNavOpen ? 'is-open' : ''}`}>
        <div className="sidebar-brand">
          <div className="sidebar-logo-mark">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="white"><path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6zm0 8a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2zm12-2h2a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z"/></svg>
          </div>
          {(!collapsed || isMobile) && (
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-.3px' }}>Daima POS</div>
              <div style={{ fontSize: 10, color: 'var(--ink4)', letterSpacing: '.3px' }}>Retail control center</div>
            </div>
          )}
          {!isMobile && (
            <button
              onClick={() => setCollapsed((value) => !value)}
              style={{ marginLeft: 'auto', padding: 4, borderRadius: 6, color: 'var(--ink3)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                {collapsed ? <path d="M9 18l6-6-6-6"/> : <path d="M15 18l-6-6 6-6"/>}
              </svg>
            </button>
          )}
          {isMobile && (
            <button className="mobile-nav-close" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation">x</button>
          )}
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}
              title={!isMobile && collapsed ? item.label : ''}
            >
              <item.icon color="currentColor" />
              {(!collapsed || isMobile) && <span className="nav-item-text">{item.label}</span>}
              {(!collapsed || isMobile) && item.badge && alertCount > 0 && (
                <span className="sidebar-badge">{alertCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-user" onClick={handleLogout} title="Sign out">
          <div className={`avatar av-sm ${avClass}`}>{user?.initials || '??'}</div>
          {(!collapsed || isMobile) && (
            <>
              <div style={{ overflow: 'hidden' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.fullName}</div>
                <div style={{ fontSize: 11, color: 'var(--ink4)', textTransform: 'capitalize' }}>{user?.role}</div>
              </div>
              <svg style={{ marginLeft: 'auto', color: 'var(--ink3)', flexShrink: 0 }} width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </>
          )}
        </div>
      </aside>

      <div className="main-area">
        <header className="topbar">
          <div className="topbar-leading">
            {isMobile && (
              <button className="btn btn-ghost btn-icon" onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
                <IconMenu color="var(--ink2)" />
              </button>
            )}
            <div>
              <div style={{ fontSize: 11.5, color: 'var(--ink3)', marginBottom: 1 }}>Daima POS</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{pageTitle}</div>
            </div>
          </div>
          <div className="topbar-actions">
            <button className="btn btn-ghost btn-icon" onClick={() => navigate('/alerts')} style={{ position: 'relative' }}>
              <IconBell color="var(--ink3)" />
              {alertCount > 0 && (
                <span style={{ position: 'absolute', top: 4, right: 4, width: 7, height: 7, background: 'var(--red)', borderRadius: '50%', border: '2px solid var(--surface)' }} />
              )}
            </button>
            <div className="topbar-user-chip">
              <div className={`avatar av-sm ${avClass}`}>{user?.initials || '??'}</div>
              {!isMobile && <div style={{ fontSize: 12.5, fontWeight: 600 }}>{user?.fullName?.split(' ')[0]}</div>}
              {!isMobile && <span className={`badge ${user?.role === 'admin' ? 'badge-blue' : user?.role === 'supervisor' ? 'badge-teal' : 'badge-gray'}`} style={{ textTransform: 'capitalize' }}>{user?.role}</span>}
            </div>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function IconGrid({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
}
function IconPOS({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M8 4v4M16 4v4M2 12h20"/></svg>;
}
function IconBox({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>;
}
function IconBag({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>;
}
function IconUsers({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
}
function IconBar({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path d="M18 20V10M12 20V4M6 20v-6"/></svg>;
}
function IconCog({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>;
}
function IconBell({ color = 'currentColor' }) {
  return <svg width="16" height="16" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
}
function IconMenu({ color = 'currentColor' }) {
  return <svg width="18" height="18" fill="none" stroke={color} strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
}

